import * as React from "react";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";
import { SearchState } from "src/search/types";
import { InputContainer } from "./InputContainer";
import { ClauseSwitch } from "./ClauseSwitch";
import { AddClause } from "./AddClause";
import { Controls } from "./Controls";
import {
  InputClause,
  Clauses,
  QuestionInputOptions,
  isDateRangeInputClause,
  isFilterConditionInputClause,
} from "./definitions";
import { cloneData } from "src/utils/objects";
import { mergeState } from "pages/[orgId]/boards/lib";
import { asKey } from "src/utils/fields";
import { useQuestionInputOptions } from "../QuestionInput";

export function ControlledQuestionInput(props: {
  alternateStyle?: boolean;
  applyLabel?: string;
  collapsed?: boolean;
  floatAway?: boolean; // only take up one line of space
  disabledApply?: boolean;
  disabledCancel?: boolean;
  expandOnFocus?: boolean;
  hideDisabled?: boolean; // Dashboards don't want to show unavailable options
  onApply?: () => void;
  onChange(value: SearchState): void;
  onError?: () => void;
  afterReset?: () => void;
  handleReset?: () => void;
  options: QuestionInputOptions;
  resetIcon?: React.ReactNode; // for customization
  resetTitle?: React.ReactNode; // for customization
  seed?: string; // this indiciates an upstream change that is  the "new" state
  snippetOptionFilter?: (snippet?: AnnotatedDeepLink) => boolean;
  snippetTransformer?: (state: SearchState) => SearchState;
  emptyText?: string;
  value: SearchState;
}) {
  const clauses = useQuestionInputClauses(
    props.seed,
    props.value,
    props.onChange,
    props.snippetTransformer
  );
  const options = useQuestionInputOptions(props.options, clauses.list);

  const [hasFocus, setHasFocus] = React.useState(false);
  let collapsed =
    props.collapsed || (props.disabledApply && props.disabledCancel);
  if (props.expandOnFocus && hasFocus) collapsed = false;
  return (
    <InputContainer
      alternateStyle={props.alternateStyle}
      collapsed={collapsed}
      floatAway={props.floatAway}
      clauses={clauses}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      addAfter={
        <Controls
          searchState={props.value}
          applyLabel={props.applyLabel}
          clauses={clauses}
          collapsed={collapsed}
          disabledApply={props.disabledApply}
          disabledCancel={props.disabledCancel}
          onApply={props.onApply}
          onReset={() => {
            if (props.handleReset) {
              props.handleReset();
            } else {
              clauses.reset();
              props.afterReset && props.afterReset();
            }
          }}
          resetIcon={props.resetIcon}
          resetTitle={props.resetTitle}
          snippetOptionFilter={props.snippetOptionFilter}
          snippetTransformer={props.snippetTransformer}
          onToggleCollapse={() => {
            console.log("toggle collapse not required in this view");
          }}
        />
      }
    >
      {clauses.list.map((clause) => (
        <ClauseSwitch
          key={clause.key}
          clause={clause}
          value={clauses.value}
          removeClause={clauses.removeClause}
          updateClause={clauses.updateClause}
        />
      ))}
      {clauses.list.length === 0 && (
        <div
          style={{
            paddingTop: 10,
            paddingLeft: 12,
            opacity: 0.6,
            pointerEvents: "none",
            fontSize: 13,
          }}
        >
          {clauses.isSeeding ? "" : props.emptyText || "Empty query"}
        </div>
      )}
      <AddClause
        clauses={clauses.list}
        onAdd={clauses.addClause}
        onMerge={clauses.mergeStateIntoClauses}
        options={options}
        snippetTransformer={props.snippetTransformer}
        hideDisabled={props.hideDisabled}
        value={props.value}
        onSetValue={clauses.setValue}
      />
    </InputContainer>
  );
}

function useQuestionInputClauses(
  _seed: string | undefined,
  searchState: SearchState,
  onChange: (value: SearchState) => void,
  onMerge?: (value: SearchState) => SearchState
): {
  addClause(
    partial: Pick<InputClause, "key" | "type" | "value">,
    status: "init" | "ready"
  ): void;
  clearClauses(): void;
  isSeeding: boolean;
  list: InputClause[];
  mergeStateIntoClauses(state: SearchState): void;
  removeClause(clause: InputClause): void;
  reset(): void;
  setValue(state: SearchState): void;
  updateClause(clause: InputClause): void;
  value: SearchState;
} {
  const userMergeState = React.useRef(onMerge || ((s: SearchState) => s));
  const [adding, setAdding] = React.useState<InputClause[]>([]);

  const addClause = React.useCallback(
    (clause: InputClause) => {
      console.log("adding", { ...clause, status: "init" });
      setAdding([...adding, { ...clause, status: "init" }]);
    },
    [adding]
  );

  const clearClauses = React.useCallback(() => {
    setAdding([]);
    onChange({} as SearchState);
  }, [onChange]);

  const removeClause = React.useCallback(
    (clause: InputClause) => {
      const update = cloneData(searchState);
      if (isDateRangeInputClause(clause)) {
        update.duration = undefined;
        update.startDate = undefined;
        update.endDate = undefined;
      } else if (isFilterConditionInputClause(clause)) {
        update.filters = update.filters?.filter(
          (_, i) => `filter-${i}` !== clause.key
        );
      }
      setAdding((adding) => adding.filter((item) => item.key !== clause.key));
      onChange(update);
    },
    [searchState, onChange]
  );

  const updateClause = React.useCallback(
    (clause: InputClause) => {
      const update = cloneData(searchState);
      if (isDateRangeInputClause(clause)) {
        update.duration = clause.value?.duration;
        update.startDate = clause.value?.startDate;
        update.endDate = clause.value?.endDate;
      } else if (isFilterConditionInputClause(clause)) {
        const mergeable = { filters: [clause.value] } as SearchState;
        update.filters = mergeState(update, mergeable).filters;
      }
      setAdding((adding) => adding.filter((item) => item.key !== clause.key));
      onChange(update);
    },
    [searchState, onChange]
  );

  const mergeStateIntoClauses = React.useCallback(
    (state: SearchState) => {
      //   console.log("merging", searchState, "into", state);
      setAdding([]);
      onChange(mergeState(userMergeState.current(searchState), state));
    },
    [searchState, onChange]
  );

  // In the other version of this control `QuestionInput`
  // this doesn't merge with the snippet merges. This version
  // is used by the new boards, and ALWAYS want to be filtered
  // like a snippet, for safety. weird things happen otherwise.
  const setValue = React.useCallback(
    (value: SearchState) => {
      setAdding([]);
      onChange(userMergeState.current(value));
    },
    [onChange]
  );

  const clauses: InputClause[] = React.useMemo(() => {
    const clauses: InputClause[] = [];
    if (searchState.duration) {
      clauses.push({
        key: "date-range-0",
        type: Clauses.DATE_RANGE,
        value: { duration: searchState.duration },
        status: "ready",
      });
    } else if (searchState.startDate && searchState.endDate) {
      clauses.push({
        key: "date-range-0",
        type: Clauses.DATE_RANGE,
        value: {
          startDate: searchState.startDate,
          endDate: searchState.endDate,
        },
        status: "ready",
      });
    } else {
      const addDateRangeClause = adding.find((clause) =>
        isDateRangeInputClause(clause)
      );
      if (addDateRangeClause) {
        clauses.push({ ...addDateRangeClause });
      }
    }
    (searchState.filters || [])
      .sort((a, b) => asKey(a).localeCompare(asKey(b)))
      .forEach((condition, i) => {
        clauses.push({
          key: `filter-${i}`,
          type: Clauses.FILTER_CONDITION,
          value: condition,
          status: "ready",
        });
      });
    adding.map((clause) => {
      if (isFilterConditionInputClause(clause)) {
        clauses.push(clause);
      }
    });
    return clauses;
  }, [
    adding,
    searchState.duration,
    searchState.filters,
    searchState.endDate,
    searchState.startDate,
  ]);

  return {
    addClause,
    clearClauses,
    isSeeding: false,
    list: clauses,
    mergeStateIntoClauses,
    removeClause,
    reset: () => {},
    setValue,
    updateClause,
    value: searchState,
  };
}
